import * as React from "react";
import {
  redirect,
  RouterProvider,
  createBrowserRouter,
  Navigate,
  useLocation,
  useParams,
  useSearchParams
} from "react-router-dom";
import { RouteErrorFallback } from "@/components/GlobalErrorBoundary";

// Legacy /copilot?q=... handler — Moshky is a drawer, not a page. Fire the
// global open event with the optional prefilled question, then bounce home.
function OpenMoshkyRedirect() {
  const [params] = useSearchParams();
  const q = params.get("q") || undefined;
  React.useEffect(() => {
    window.dispatchEvent(new CustomEvent("velaro:open-moshky", { detail: { question: q } }));
  }, [q]);
  return <Navigate to="/" replace />;
}
import { PrivateRoute } from "./PrivateRoute";
import { VelaroAdminRoute } from "./VelaroAdminRoute";
import { SupportAdminRoute } from "./SupportAdminRoute";
import { GatedAdminFeature, GatedSubscriptionFeature, GatedEntitlementFeature } from "@/pages/Messaging/Integrations/shared/GatedIntegration";
import { INTEGRATION_META } from "@/data/integrationUpgradeMeta";
import { BackToIntegrations } from "@/pages/Messaging/Integrations/shared/IntegrationPageWrapper";
import AuthService from "@/services/AuthService";
import { RouteHeader } from "@velaro/velaro-component-lib";
import AgreementGate from "@/components/AgreementGate";
import StickyRouteHeader from "@/pages/Shared/StickyRouteHeader";
import AINewSettingsTabs from "@/pages/Messaging/Bots/AINew/AINewSettingsTabs";

// Tab bar rendered inside StickyRouteHeader for /ai-new/settings/*/:id routes.
// Must call useParams() here (within the route element tree) to pick up :id.
function AIConfigSettingsTabs() {
  const { id } = useParams();
  if (!id) return null;
  return <AINewSettingsTabs aiConfigId={id} />;
}

// Header action components — must be eager so they render before the lazy page resolves
import CreateChatbotButton from "@/pages/Messaging/Bots/AI/CreateChatbotButton";
import NewWorkflowButton from "@/pages/Messaging/Workflows/NewWorkflowButton";
import SaveChatbotButton from "@/pages/Messaging/Bots/AI/SaveChatbotButton";
import CreateChatWindowButton from "@/pages/Messaging/ChatWindow/CreateChatWindowButton";
import AINewCreateChatbotButton from "@/pages/Messaging/Bots/AINew/AINewCreateChatbotButton";
import AINewSaveChatbotButton from "@/pages/Messaging/Bots/AINew/AINewSaveChatbotButton";
import CreateIndexButton from "@/pages/Messaging/Bots/AI/CreateIndexButton";
import AIChatbotTrainingSettingsTabs from "@/pages/Messaging/Bots/AI/AIChatbotTrainingSettingsTab";
import AddPhoneNumberButton from "@/pages/Messaging/Channels/AddPhoneNumberButton";

import { SecondaryButton } from "@/components/ds";

// Integration pages with named BackTo exports — static import required because the named
// export is used directly in headerActions (a static import forces the module to load eagerly;
// splitting these would require moving BackTo components to separate files).
import NetSuite, {
  BackToIntegrationLanding
} from "@/pages/Messaging/Integrations/NetSuite/NetSuite";
import UserProfile, {
  BackToUsersButton
} from "@/pages/Messaging/Users/UserProfile";
import ShopifyCreate, {
  ShopifyBackToShopifyOverview
} from "@/pages/Messaging/Integrations/Shopify/ShopifyCreate";
import ShopifyOverview, {
  ShopifyBackToIntegrationLanding
} from "@/pages/Messaging/Integrations/Shopify/ShopifyOverview";
import HubSpot, {
  BackToIntegrationLandingHubSpot
} from "@/pages/Messaging/Integrations/HubSpot/HubSpot";
import HubSpotOverview, {
  HubSpotBackToIntegrationLanding,
  HubSpotBackToHubSpotOverview
} from "@/pages/Messaging/Integrations/HubSpot/HubSpotOverview";
import Salesforce from "@/pages/Messaging/Integrations/Salesforce/Salesforce";
import ServiceNow, {
  BackToIntegrationLandingServiceNow
} from "@/pages/Messaging/Integrations/ServiceNow/ServiceNow";
import Uplisting, {
  BackToIntegrationLandingUplisting
} from "@/pages/Messaging/Integrations/Uplisting/Uplisting";
import Guesty, {
  BackToIntegrationLandingGuesty
} from "@/pages/Messaging/Integrations/Guesty/Guesty";
import Cloudbeds, {
  BackToIntegrationLandingCloudbeds
} from "@/pages/Messaging/Integrations/Cloudbeds/Cloudbeds";
import Pipedrive, {
  BackToIntegrationLandingPipedrive
} from "@/pages/Messaging/Integrations/Pipedrive/Pipedrive";
import QuickBooks, {
  BackToIntegrationLandingQuickBooks
} from "@/pages/Messaging/Integrations/QuickBooks/QuickBooks";
import Zoho, {
  BackToIntegrationLandingZoho
} from "@/pages/Messaging/Integrations/Zoho/Zoho";
import Toast, {
  BackToIntegrationLandingToast
} from "@/pages/Messaging/Integrations/Toast/Toast";
import Clover, {
  BackToIntegrationLandingClover
} from "@/pages/Messaging/Integrations/Clover/index";
import Mindbody, {
  BackToIntegrationLandingMindbody
} from "@/pages/Messaging/Integrations/Mindbody/Mindbody";
import Monday, {
  BackToIntegrationLandingMonday
} from "@/pages/Messaging/Integrations/Monday/Monday";
import Recruiting, {
  BackToIntegrationLandingRecruiting
} from "@/pages/Messaging/Integrations/Recruiting/Recruiting";
import Jobber, {
  BackToIntegrationLandingJobber
} from "@/pages/Messaging/Integrations/Jobber/index";
import CallRail, {
  BackToIntegrationLandingCallRail
} from "@/pages/Messaging/Integrations/CallRail/CallRail";
import MicrosoftBookings, {
  BackToIntegrationLandingMicrosoftBookings
} from "@/pages/Messaging/Integrations/MicrosoftBookings/MicrosoftBookings";
import OpenTableIntegration, {
  BackToIntegrationLandingOpenTable
} from "@/pages/Messaging/Integrations/OpenTable/index";
import ResyIntegration, {
  BackToIntegrationLandingResy
} from "@/pages/Messaging/Integrations/Resy/index";
import BigCommerce, {
  BackToIntegrationLandingBigCommerce
} from "@/pages/Messaging/Integrations/BigCommerce/BigCommerce";
import WooCommerce, {
  BackToIntegrationLandingWooCommerce
} from "@/pages/Messaging/Integrations/WooCommerce/WooCommerce";
import BigCommerceOverview, {
  BigCommerceBackToIntegrationLanding,
  BigCommerceBackToOverview
} from "@/pages/Messaging/Integrations/BigCommerce/BigCommerceOverview";
import Magento, {
  BackToIntegrationLandingMagento
} from "@/pages/Messaging/Integrations/Magento/Magento";
import MagentoOverview, {
  MagentoBackToIntegrationLanding,
  MagentoBackToOverview
} from "@/pages/Messaging/Integrations/Magento/MagentoOverview";
import Dynamics, {
  BackToIntegrationLandingDynamics
} from "@/pages/Messaging/Integrations/Dynamics/Dynamics";
import DynamicsOverview, {
  DynamicsBackToIntegrationLanding,
  DynamicsBackToOverview
} from "@/pages/Messaging/Integrations/Dynamics/DynamicsOverview";
import ZendeskOverview, {
  ZendeskBackToIntegrationLanding
} from "@/pages/Messaging/Integrations/Zendesk/ZendeskOverview";
import NativeCrm, {
  BackToIntegrationLandingNativeCrm
} from "@/pages/Messaging/Integrations/NativeCrm/NativeCrm";
import KlaviyoIntegration from "@/pages/Messaging/Integrations/Klaviyo/Klaviyo";
import Vast, {
  BackToIntegrationLandingVast
} from "@/pages/Messaging/Integrations/Vast/Vast";
import NextivaPage, {
  BackToIntegrationLandingNextiva
} from "@/pages/Messaging/Integrations/Nextiva/Nextiva";
import TeamsPhonePage, {
  BackToIntegrationLandingTeamsPhone
} from "@/pages/Messaging/Integrations/TeamsPhone/TeamsPhone";
import SkySwitchPage, {
  BackToIntegrationLandingSkySwitch
} from "@/pages/Messaging/Integrations/SkySwitch/SkySwitch";
import SecureFormsPanel, {
  BackToIntegrationLandingSecureForms
} from "@/pages/Messaging/Integrations/SecureForms/SecureFormsPanel";
import GorgiasWizard, {
  BackToIntegrationLandingGorgias
} from "@/pages/Messaging/Integrations/Gorgias/GorgiasWizard";
import GoogleTagManager from "@/pages/Messaging/Integrations/GoogleTagManager/GoogleTagManager";
import GA4Analytics from "@/pages/Messaging/Integrations/GA4/GA4Analytics";
const ServiceTitanPage = React.lazy(() => import("@/pages/Messaging/Integrations/ServiceTitan/ServiceTitan"));
const AirtablePage = React.lazy(() => import("@/pages/Messaging/Integrations/Airtable/Airtable"));
const NotionPage = React.lazy(() => import("@/pages/Messaging/Integrations/Notion/Notion"));

// Lazy-loaded page components — Vite code-splits these into separate chunks.
// Each chunk only downloads when the user navigates to that route.
const ContactCenter = React.lazy(() => import("@/pages/ContactCenter"));
const Home = React.lazy(() => import("@/pages/Home"));
const AccountUsage = React.lazy(() => import("@/pages/Account/Usage"));
const EntraCallback = React.lazy(() => import("@/pages/Auth/EntraCallback"));
const LoginCallback = React.lazy(() => import("@/pages/Auth/LoginCallback"));
const LoginCallbackError = React.lazy(() => import("@/pages/Auth/LoginCallbackError"));
const LoginUnauthorized = React.lazy(() => import("@/pages/Auth/LoginUnauthorized"));
const Logout = React.lazy(() => import("@/pages/Auth/Logout"));
const LogoutCallback = React.lazy(() => import("@/pages/Auth/LogoutCallback"));
const AutoResponses = React.lazy(() => import("@/pages/Messaging/AutoResponses/AutoResponses"));
const CannedResponses = React.lazy(() => import("@/pages/Messaging/CannedResponses/CannedResponses"));
const TwitterCallback = React.lazy(() => import("@/pages/Messaging/Channels/TwitterCallback"));
const IntegrationLog = React.lazy(() => import("@/pages/Messaging/Integrations/Log"));
const NetSuiteCreate = React.lazy(() => import("@/pages/Messaging/Integrations/NetSuite/NetSuiteCreate"));
const NetSuiteCallback = React.lazy(() => import("@/pages/Messaging/Integrations/NetSuite/NetSuiteCallback"));
const NetSuiteOAuth2Callback = React.lazy(() => import("@/pages/Messaging/Integrations/NetSuite/NetSuiteOAuth2Callback"));
const Surveys = React.lazy(() => import("@/pages/Messaging/Surveys/Surveys"));
const Teams = React.lazy(() => import("@/pages/Messaging/Teams/Teams"));
const TeamsRouting = React.lazy(() => import("@/pages/Messaging/Settings/TeamsRouting"));
const RoutingOpsDashboard = React.lazy(() => import("@/pages/Messaging/Settings/RoutingOpsDashboard"));
/* DEAD — Web Messenger Style/Language Config replaced by Chat Window Designer (/chat_window) +
   Rules engine (determine-chat-window). Kept for 30d revival.
const WebMessenger = React.lazy(() => import("@/pages/Messaging/WebMessenger")); */
const WorkSchedules = React.lazy(() => import("@/pages/Messaging/WorkSchedules/WorkSchedules"));
const Users = React.lazy(() => import("@/pages/Messaging/Users/Users"));
const AddUserWizard = React.lazy(() => import("@/pages/Messaging/Users/AddUserWizard/AddUserWizard"));
const Deployment = React.lazy(() => import("@/pages/Messaging/Deployments/Deployments"));
const ChatExperience = React.lazy(() => import("@/pages/Messaging/ChatExperience/ChatExperience"));
const Schedules = React.lazy(() => import("@/pages/Messaging/Schedules/Schedules"));
const BotManager = React.lazy(() => import("@/pages/Messaging/Bots"));
const RuleManager = React.lazy(() => import("@/pages/Messaging/Rules"));
const RoutingHistory = React.lazy(() => import("@/pages/Messaging/Rules/RoutingHistory"));
const RoutingSettings = React.lazy(() => import("@/pages/Routing/RoutingSettings"));
const RoutingDiagnosticsPage = React.lazy(() => import("@/pages/Routing/RoutingDiagnosticsPage"));
const RoutingActivity = React.lazy(() => import("@/pages/Routing/RoutingActivity"));
const WhatsRunning = React.lazy(() => import("@/pages/Messaging/WhatsRunning/WhatsRunning"));
const ChatPreview = React.lazy(() => import("@/pages/Messaging/ChatPreview/ChatPreview"));
const Landing = React.lazy(() => import("@/pages/Messaging/Integrations/IntegrationsLanding"));
const FileStorage = React.lazy(() => import("@/pages/Messaging/FileStorage"));
// WorkflowsEditor and WorkflowsCreate include @xyflow/react (canvas lib) — deferring saves ~500KB on initial load
const WorkflowsCreate = React.lazy(() => import("@/pages/Messaging/Workflows/WorkflowsCreate"));
const Workflows = React.lazy(() => import("@/pages/Messaging/Workflows/Workflows"));
const WorkflowsLandingGate = React.lazy(() => import("@/pages/Messaging/Workflows/WorkflowsLandingGate"));
const WorkflowsEditor = React.lazy(() => import("@/pages/Messaging/Workflows/WorkflowsEditor"));
const VariableGuide = React.lazy(() => import("@/pages/Messaging/Workflows/VariableGuide"));
const SettingsHub = React.lazy(() => import("@/pages/SettingsHub/SettingsHub"));
const ConditionPresetsPage = React.lazy(() => import("@/pages/Settings/ConditionPresets/ConditionPresetsPage"));
const FocusInbox = React.lazy(() => import("@/pages/FocusInbox/FocusInbox"));
const CompliancePage = React.lazy(() => import("@/pages/Compliance/CompliancePage"));
const FacebookIntegration = React.lazy(() => import("@/pages/Messaging/Channels/FacebookIntegration"));
const InstagramIntegration = React.lazy(() => import("@/pages/Messaging/Channels/InstagramIntegration"));
const TikTokIntegration = React.lazy(() => import("@/pages/Messaging/Channels/TikTokIntegration"));
const TikTokCallback = React.lazy(() => import("@/pages/Messaging/Channels/TikTokCallback"));
const SmsIntegration = React.lazy(() => import("@/pages/Messaging/Channels/SmsIntegration"));
const WhatsAppIntegration = React.lazy(() => import("@/pages/Messaging/Channels/WhatsAppIntegration"));
const RcsIntegration = React.lazy(() => import("@/pages/Messaging/Channels/RcsIntegration"));
const AppleMessagesIntegration = React.lazy(() => import("@/pages/Messaging/Channels/AppleMessagesIntegration"));
const IvrIntegration = React.lazy(() => import("@/pages/Messaging/Channels/IvrIntegration"));
// ArticlesCreate and ArticlesEdit include CKEditor5 + TipTap — deferring saves ~2MB on initial load
const ArticlesIndex = React.lazy(() => import("@/kb/articles/Index"));
const ArticlesCreate = React.lazy(() => import("@/kb/articles/Create"));
const ArticlesEdit = React.lazy(() => import("@/kb/articles/Edit"));
const SettingsIndex = React.lazy(() => import("@/kb/settings/Index"));
const TopicsIndex = React.lazy(() => import("@/kb/topics/Index"));
const SiteMap = React.lazy(() => import("@/kb/sitemap/Index"));
const SetupIndex = React.lazy(() => import("@/kb/setup/Index"));
const KBPerformance = React.lazy(() => import("@/kb/performance/Index"));
const KBWidgetEmbedCode = React.lazy(() => import("@/kb/widget/EmbedCode"));
const AssetsIndex = React.lazy(() => import("@/kb/assets/Index"));
const KBMigrate = React.lazy(() => import("@/kb/migrate/Index"));
const Skills = React.lazy(() => import("@/pages/Messaging/Skills/Skills"));
const MessagingSetup = React.lazy(() => import("@/pages/Messaging/Setup/Index"));
// Admin pages — rarely accessed, no reason to pay their cost on initial load
const ActivityLog = React.lazy(() => import("@/pages/Admin/ActivityLog"));
const ToolUsageReport = React.lazy(() => import("@/pages/Admin/ToolUsageReport"));
const VelaroLogger = React.lazy(() => import("@/pages/Admin/VelaroLogger"));
const AgentLoginLogs = React.lazy(() => import("@/pages/Admin/AgentLoginLogs"));
const UserRoles = React.lazy(() => import("@/pages/Messaging/UserRoles"));
const CopilotAdmin = React.lazy(() => import("@/pages/Admin/CopilotAdmin"));
const PricingQuote = React.lazy(() => import("@/pages/Admin/PricingQuote"));
const DatabaseTool = React.lazy(() => import("@/pages/Admin/DatabaseTool"));
const DeployHistory = React.lazy(() => import("@/pages/Superadmin/DeployHistory"));
const InternalDocsViewer = React.lazy(() => import("@/pages/Superadmin/InternalDocsViewer"));
const WorkflowLogs = React.lazy(() => import("@/pages/Superadmin/WorkflowLogs"));
const BackgroundJobMigration = React.lazy(() => import("@/pages/Superadmin/BackgroundJobMigration"));
const SkillTracesPage = React.lazy(() => import("@/pages/Superadmin/SkillTraces/SkillTracesPage"));
const FeaturePackagesPanel = React.lazy(() => import("@/pages/Superadmin/FeaturePackagesPanel"));
const PricingCatalogEditor = React.lazy(() => import("@/pages/Superadmin/PricingCatalogEditor"));
const PolicyNotices = React.lazy(() => import("@/pages/Superadmin/PolicyNotices"));
const SkillRoutingOverridesPage = React.lazy(() => import("@/pages/Superadmin/SkillRoutingOverrides/SkillRoutingOverridesPage"));
const SearchIndexes = React.lazy(() => import("@/pages/Admin/SearchIndexes"));
const CampaignHealth = React.lazy(() => import("@/pages/Admin/CampaignHealth"));
const DetectionReport = React.lazy(() => import("@/pages/Admin/DetectionReport"));
const PlatformChannelSetup = React.lazy(() => import("@/pages/Admin/PlatformChannelSetup"));
const DesignSystemV2 = React.lazy(() => import("@/pages/Admin/DesignSystemV2"));
const IvrPlatformSettings = React.lazy(() => import("@/pages/Admin/IvrPlatformSettings"));
/* DEAD — MF Athletic Demo removed from nav. Route kept for direct URL access. Kept for 30s revival.
const MFAthleticDemo = React.lazy(() => import("@/pages/Admin/MFAthleticDemo")); */
const LiveMonitor = React.lazy(() => import("@/pages/Admin/LiveMonitor"));
const AccountHealth = React.lazy(() => import("@/pages/Admin/AccountHealth"));
const AccountManagerAssignments = React.lazy(() => import("@/pages/Admin/AccountManagerAssignments"));
const AIPromptPolicy = React.lazy(() => import("@/pages/Admin/AIPromptPolicy"));
const AppMarketplace = React.lazy(() => import("@/pages/Admin/AppMarketplace/AppMarketplace"));
const SiteSync = React.lazy(() => import("@/pages/Admin/SiteSync"));
const Deployments = React.lazy(() => import("@/pages/Admin/Deployments"));
const SubscriptionSettings = React.lazy(() => import("@/pages/Admin/SubscriptionSettings"));
const BillingFinancial = React.lazy(() => import("@/pages/Admin/BillingFinancial"));
const BillingStaff = React.lazy(() => import("@/pages/Admin/BillingStaff"));
const BillingPlans = React.lazy(() => import("@/pages/Admin/BillingPlans"));
const BillingOverage = React.lazy(() => import("@/pages/Admin/BillingOverage"));
const PlansAndPricing = React.lazy(() => import("@/pages/Admin/PlansAndPricing"));
const BillingGuide = React.lazy(() => import("@/pages/Admin/BillingGuide"));
const SubscriptionSetupWizard = React.lazy(() => import("@/pages/Admin/SubscriptionSetupWizard"));
const KBDomains = React.lazy(() => import("@/pages/Admin/KBDomains"));
const HangfireMonitor = React.lazy(() => import("@/pages/Admin/HangfireMonitor"));
const SystemHealth = React.lazy(() => import("@/pages/Admin/SystemHealth"));
const SupportTools = React.lazy(() => import("@/pages/Admin/SupportTools"));
// Bot / AI pages
const AI = React.lazy(() => import("@/pages/Messaging/Bots/AI/AI"));
const AIChatbotSettingsGeneral = React.lazy(() => import("@/pages/Messaging/Bots/AI/AIChatbotSettingsGeneral"));
const AIChatbotSettingsTraining = React.lazy(() => import("@/pages/Messaging/Bots/AI/AIChatbotSettingsTraining"));
const AIChatbotSettingsHandoff = React.lazy(() => import("@/pages/Messaging/Bots/AI/AIChatbotSettingsHandoff"));
const AIChatbotSettingsTest = React.lazy(() => import("@/pages/Messaging/Bots/AI/AIChatbotSettingsTest"));
const AIChatbotSettingsKnowledgeOverrides = React.lazy(() => import("@/pages/Messaging/Bots/AI/AIChatbotSettingsKnowledgeOverrides"));
const AIChatbotSettingsSuppressedContent = React.lazy(() => import("@/pages/Messaging/Bots/AI/AIChatbotSettingsSuppressedContent"));
const AIChatbotSettingsSkills = React.lazy(() => import("@/pages/Messaging/Bots/AI/AIChatbotSettingsSkills"));
const AIChatbotTrainingIndex = React.lazy(() => import("@/pages/Messaging/Bots/AI/AIChatbotTrainingIndex"));
const AIChatbotSettingsTrainingTabFile = React.lazy(() => import("@/pages/Messaging/Bots/AI/AIChatbotFileTrainingIndex"));
const AIChatbotFileTraining = React.lazy(() => import("@/pages/Messaging/Bots/AI/AIChatbotFileTraining"));
const AIChatbotWebTraining = React.lazy(() => import("@/pages/Messaging/Bots/AI/AIChatbotWebTraining"));
const BuildBotWizard = React.lazy(() => import("@/pages/Messaging/Bots/BuildBotWizard"));
const LanguageSetupWizard = React.lazy(() => import("@/pages/Messaging/LanguageSetupWizard/LanguageSetupWizard"));
const AINewTraining = React.lazy(() => import("@/pages/Messaging/Bots/AINew/AINewTraining"));
// DEAD 2026-05-24 — legacy index-only test panel, replaced by AINewSettingsPlayground + TestIndexesPage.
// Kept for 30s revival per CLAUDE.md "COMMENT OUT DEAD PAGES" rule.
// const AINewChat = React.lazy(() => import("@/pages/Messaging/Bots/AINew/AINewChat"));
const AINewSettings = React.lazy(() => import("@/pages/Messaging/Bots/AINew/AINewSettings"));
const AINewSettingsGeneral = React.lazy(() => import("@/pages/Messaging/Bots/AINew/AINewSettingsGeneral"));
const AINewSettingsPersona = React.lazy(() => import("@/pages/Messaging/Bots/AINew/AINewSettingsPersona"));
const AINewSettingsHandoff = React.lazy(() => import("@/pages/Messaging/Bots/AINew/AINewSettingsHandoff"));
const AINewSettingsPlayground = React.lazy(() => import("@/pages/Messaging/Bots/AINew/AINewSettingsPlayground"));
// DEAD 2026-05-25 — collapsed into AINewSettingsPlayground (now has config + indexes pickers).
// Kept for 30s revival per CLAUDE.md "COMMENT OUT DEAD PAGES" rule.
// const TestIndexesPage = React.lazy(() => import("@/pages/Messaging/Bots/AINew/TestIndexesPage"));
const AINewIndexes = React.lazy(() => import("@/pages/Messaging/Bots/AINew/AINewIndexes"));
const AINewSettingsKnowledgeOverrides = React.lazy(() => import("@/pages/Messaging/Bots/AINew/AINewSettingsKnowledgeOverrides"));
const AINewSettingsKnowledge = React.lazy(() => import("@/pages/Messaging/Bots/AINew/AINewSettingsKnowledge"));
const AINewSettingsSkills = React.lazy(() => import("@/pages/Messaging/Bots/AINew/AINewSettingsSkills"));
const AINewSettingsChannelOverrides = React.lazy(() => import("@/pages/Messaging/Bots/AINew/AINewSettingsChannelOverrides"));
const AINewSettingsQnA = React.lazy(() => import("@/pages/Messaging/Bots/AINew/AINewSettingsQnA"));
// Integration pages without named headerAction exports
const AccelaCivic = React.lazy(() => import("@/pages/Messaging/Integrations/Accela/Accela"));
const AccelaCivicCreate = React.lazy(() => import("@/pages/Messaging/Integrations/Accela/AccelaCreate"));
const AccelaCallbackBridge = React.lazy(() => import("@/pages/Messaging/Integrations/Accela/AccelaCallbackBridge"));
const Formstack = React.lazy(() => import("@/pages/Messaging/Integrations/Formstack/Formstack"));
const FormstackCreate = React.lazy(() => import("@/pages/Messaging/Integrations/Formstack/FormstackCreate"));
const ServiceNowCreate = React.lazy(() => import("@/pages/Messaging/Integrations/ServiceNow/ServiceNowCreate"));
const SAP = React.lazy(() => import("@/pages/Messaging/Integrations/SAP/SAP"));
const SapCreate = React.lazy(() => import("@/pages/Messaging/Integrations/SAP/SapCreate"));
const UplistingCreate = React.lazy(() => import("@/pages/Messaging/Integrations/Uplisting/UplistingCreate"));
const GuestyCreate = React.lazy(() => import("@/pages/Messaging/Integrations/Guesty/GuestyCreate"));
const CloudbedsCreate = React.lazy(() => import("@/pages/Messaging/Integrations/Cloudbeds/CloudbedsCreate"));
const MindbodyCreate = React.lazy(() => import("@/pages/Messaging/Integrations/Mindbody/MindbodyCreate"));
const HubSpotCreate = React.lazy(() => import("@/pages/Messaging/Integrations/HubSpot/HubSpotCreate"));
const BigCommerceCreate = React.lazy(() => import("@/pages/Messaging/Integrations/BigCommerce/BigCommerceCreate"));
const MagentoCreate = React.lazy(() => import("@/pages/Messaging/Integrations/Magento/MagentoCreate"));
const DynamicsCreate = React.lazy(() => import("@/pages/Messaging/Integrations/Dynamics/DynamicsCreate"));
const Shopify = React.lazy(() => import("@/pages/Messaging/Integrations/Shopify/Shopify"));
const ShopifyInstall = React.lazy(() => import("@/pages/Messaging/Integrations/Shopify/ShopifyInstall"));
const McpServers = React.lazy(() => import("@/pages/Messaging/Integrations/MCP/McpServers"));
const CopilotStudioPage = React.lazy(() => import("@/pages/Messaging/Integrations/CopilotStudio/CopilotStudio"));
const CopilotForSalesPage = React.lazy(() => import("@/pages/Messaging/Integrations/CopilotForSales/CopilotForSales"));
const StripePayments = React.lazy(() => import("@/pages/Messaging/Integrations/Stripe"));
const PowerAutomate = React.lazy(() => import("@/pages/Messaging/Integrations/PowerAutomate"));
const MakeIntegration = React.lazy(() => import("@/pages/Messaging/Integrations/Make"));
const ZapierIntegration = React.lazy(() => import("@/pages/Messaging/Integrations/Zapier"));
const TranscriptStorage = React.lazy(() => import("@/pages/Messaging/Integrations/TranscriptStorage"));
const Microsoft365 = React.lazy(() => import("@/pages/Messaging/Integrations/Microsoft365"));
const TeamsNotifications = React.lazy(() => import("@/pages/Messaging/Integrations/Microsoft365/TeamsNotifications"));
const OneDriveSettingsPage = React.lazy(() => import("@/pages/Messaging/Integrations/Microsoft365/OneDriveSettings"));
const SlackNotificationsPage = React.lazy(() => import("@/pages/Messaging/Integrations/SlackNotifications"));
const AlertRulesPage = React.lazy(() => import("@/pages/Messaging/Integrations/AlertRules"));
const GmailInbox = React.lazy(() => import("@/pages/Messaging/Integrations/GmailInbox"));
const OutlookInbox = React.lazy(() => import("@/pages/Messaging/Integrations/OutlookInbox"));
const OutlookCalendar = React.lazy(() => import("@/pages/Messaging/Integrations/OutlookCalendar"));
const CalendlyIntegration = React.lazy(() => import("@/pages/Messaging/Integrations/Calendly"));
const CustomCommerceIntegration = React.lazy(() => import("@/pages/Messaging/Integrations/CustomCommerce"));
const CallbackSchedulingSetupWizard = React.lazy(() => import("@/pages/Messaging/Integrations/CallbackSchedulingSetupWizard"));
const CallbackSchedulingReport = React.lazy(() => import("@/pages/Messaging/Integrations/CallbackSchedulingReport"));
const AcuityIntegration = React.lazy(() => import("@/pages/Messaging/Integrations/Acuity"));
const SquareIntegration = React.lazy(() => import("@/pages/Messaging/Integrations/Square"));
const SquareCallbackPage = React.lazy(() => import("@/pages/Messaging/Integrations/Square/Callback"));
const Webflow = React.lazy(() => import("@/pages/Messaging/Integrations/Webflow/Webflow"));
const WebflowConnect = React.lazy(() => import("@/pages/Messaging/Integrations/Webflow/WebflowConnect"));
// Chat UI pages
const ChatWindow = React.lazy(() => import("@/pages/Messaging/ChatWindow"));
const ChatWindowEditor = React.lazy(() => import("@/pages/Messaging/ChatWindow/ChatWindowEditor"));
// Other pages
const CustomFields = React.lazy(() => import("@/pages/ContactCenter/CustomFields"));
const ContactSegments = React.lazy(() => import("@/pages/ContactCenter/ContactSegments"));
const TeamsChannel = React.lazy(() => import("@/pages/Messaging/Integrations/Teams/TeamsChannel"));
const MessagingSettings = React.lazy(() => import("@/pages/Messaging/Settings"));
const BillingPage = React.lazy(() => import("@/pages/Messaging/Billing"));
const SiteSettings = React.lazy(() => import("@/pages/Messaging/SiteSettings"));
const TranscriptSettings = React.lazy(() => import("@/pages/Messaging/SiteSettings/TranscriptSettings"));
const AIProviderSettings = React.lazy(() => import("@/pages/Messaging/SiteSettings/AIProviderSettings"));
const Tags = React.lazy(() => import("@/pages/Messaging/Tags/Tags"));
const Invitations = React.lazy(() => import("@/pages/Messaging/Invitations"));
const BaseInvitationEditor = React.lazy(() => import("@/pages/Messaging/Invitations/BaseInvitationEditor"));
const PageWidgets = React.lazy(() => import("@/pages/Messaging/PageWidgets"));
const ProactiveEngagement = React.lazy(() => import("@/pages/Messaging/ProactiveEngagement"));
const VisitorMonitor = React.lazy(() => import("@/pages/Messaging/VisitorMonitor"));
// EmailCampaigns includes grapesjs (email editor) — deferring saves ~800KB on initial load
const EmailCampaigns = React.lazy(() => import("@/pages/Messaging/EmailCampaigns"));
const IvrCampaigns = React.lazy(() => import("@/pages/Messaging/IvrCampaigns"));
const Appointments = React.lazy(() => import("@/pages/Appointments/Appointments"));
const Packages = React.lazy(() => import("@/pages/Messaging/Packages"));
const ContactSupport = React.lazy(() => import("@/pages/Support/ContactSupport"));
// Reports pages include recharts — deferring keeps charting code out of the initial bundle
const Dashboard = React.lazy(() => import("@/pages/Dashboard"));
const ValueReport = React.lazy(() => import("@/pages/Reports/ValueReport"));
const AgentPerformance = React.lazy(() => import("@/pages/Reports/AgentPerformance"));
const Leaderboard = React.lazy(() => import("@/pages/Reports/Leaderboard"));
const ChatIntelligence = React.lazy(() => import("@/pages/Reports/ChatIntelligence"));
const BotEffectiveness = React.lazy(() => import("@/pages/Reports/BotEffectiveness"));
const CsatReport = React.lazy(() => import("@/pages/Reports/CsatReport"));
const ConversionReport = React.lazy(() => import("@/pages/Reports/ConversionReport"));
const AppleMessagesReport = React.lazy(() => import("@/pages/Reports/AppleMessagesReport"));
const IvrCampaignReport = React.lazy(() => import("@/pages/Reports/IvrCampaignReport"));
const CallCenterReport = React.lazy(() => import("@/pages/Reports/CallCenterReport"));
const AgentConnectivityReport = React.lazy(() => import("@/pages/Reports/AgentConnectivityReport"));
const CallAttributionReport = React.lazy(() => import("@/pages/Reports/CallAttributionReport"));
const CallActivityReport = React.lazy(() => import("@/pages/Reports/CallActivityReport"));
const Conversions = React.lazy(() => import("@/pages/Messaging/Settings/Conversions"));
const ClickToCallPage = React.lazy(() => import("@/pages/Messaging/ClickToCall"));
const AvatarSettings = React.lazy(() => import("@/pages/Messaging/Avatar/AvatarSettings"));
// Billing pages
const Contracts = React.lazy(() => import("@/pages/Billing/Contracts"));
const ContractTemplates = React.lazy(() => import("@/pages/Billing/ContractTemplates"));
const SigningPage = React.lazy(() => import("@/pages/Billing/SigningPage"));
const BillingInvoices = React.lazy(() => import("@/pages/Billing/Invoices"));
const BillingTemplates = React.lazy(() => import("@/pages/Billing/InvoiceTemplates"));
const BillingStripeSettings = React.lazy(() => import("@/pages/Billing/StripeSettings"));
const BillingEventLog = React.lazy(() => import("@/pages/Billing/BillingEventLog"));
const BillingUsagePacks = React.lazy(() => import("@/pages/Billing/UsagePacks"));
const BillingCampaignUsage = React.lazy(() => import("@/pages/Billing/CampaignUsage"));
const BillingQbMapping = React.lazy(() => import("@/pages/Billing/QbMapping"));
const QuoteAcceptPage = React.lazy(() => import("@/pages/Billing/QuoteAcceptPage"));
const QuotePublicPage = React.lazy(() => import("@/pages/Billing/QuotePublicPage"));
const BillingContacts = React.lazy(() => import("@/pages/Billing/BillingContacts"));
const PendingContracts = React.lazy(() => import("@/pages/Billing/PendingContracts"));
const NotFoundPage = React.lazy(() => import("@/pages/Shared/NotFoundPage"));
const MarketplaceActivation = React.lazy(() => import("@/pages/Marketplace/MarketplaceActivation"));

// Redirect helper — reads :id from the route and sends to the equivalent /ai-new/ tab
function RedirectAIToNew({ suffix }: { suffix: string }) {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={`/ai-new/settings/${suffix}/${id ?? ""}`} replace />;
}

function RedirectToPlayground() {
  const { id } = useParams<{ id: string }>();
  const { search } = useLocation();
  return <Navigate to={`/ai-new/playground/${id ?? ""}${search || ""}`} replace />;
}

const handleAccountSearch = (searchText: string) => {
  window.dispatchEvent(
    new CustomEvent("accountSearch", { detail: searchText })
  );
};

const createAppRoutes = (mainLoader: (params: any) => any) => [
  { path: "auth/callback", element: <EntraCallback /> },
  { path: "login/callback", element: <LoginCallback /> },
  { path: "login/callback/error", element: <LoginCallbackError /> },
  { path: "sign/:token", element: <SigningPage /> },
  { path: "quote/:token", element: <QuoteAcceptPage /> },
  { path: "q/:token", element: <QuotePublicPage /> },
  { path: "login/unauthorized", element: <LoginUnauthorized /> },
  { path: "logout", element: <Logout /> },
  { path: "logout/callback", element: <LogoutCallback /> },
  // AppSource marketplace activation — must be public (buyers arrive before they have a Velaro account)
  { path: "marketplace/activate", element: <MarketplaceActivation /> },
  {
    element: <PrivateRoute />,
    children: [
      { path: "/", element: <Home /> },
      {
        path: "/dashboard",
        element: <Dashboard />
      },
      {
        path: "/account",
        element: <SiteSettings />
      },
      {
        path: "/account/usage",
        element: <AccountUsage />
      },
      {
        path: "/account/transcript-settings",
        element: (
          <StickyRouteHeader title="Transcript Settings">
            <TranscriptSettings />
          </StickyRouteHeader>
        )
      },
      {
        path: "/deploy-history",
        element: <Navigate to="/admin/deploy-history" replace />
      },
      {
        path: "/admin/deploy-history",
        element: <SupportAdminRoute><DeployHistory /></SupportAdminRoute>
      },
      {
        path: "/admin/internal-docs/:slug",
        element: <SupportAdminRoute><InternalDocsViewer /></SupportAdminRoute>
      },
      {
        path: "/admin/workflow-logs",
        element: <SupportAdminRoute><WorkflowLogs /></SupportAdminRoute>
      },
      {
        // Background Job Migration - SuperAdmin health dashboard for the 5 timer->Hangfire jobs
        path: "/admin/background-jobs",
        element: <SupportAdminRoute><BackgroundJobMigration /></SupportAdminRoute>
      },
      {
        // Skill Framework v2 — SuperAdmin trace browser (Phase 2c)
        path: "/admin/skill-traces",
        element: <SupportAdminRoute><SkillTracesPage /></SupportAdminRoute>
      },
      {
        // Feature Packages — first-class feature catalog (SuperAdmin)
        path: "/super-admin/feature-packages",
        element: <VelaroAdminRoute><FeaturePackagesPanel /></VelaroAdminRoute>
      },
      {
        // Feature Packages — per-site entitlements (SuperAdmin)
        path: "/super-admin/sites/:siteId/features",
        element: <VelaroAdminRoute><FeaturePackagesPanel /></VelaroAdminRoute>
      },
      {
        // Pricing Catalog — edit prices and create packages without a code deploy
        path: "/super-admin/pricing-catalog",
        element: <VelaroAdminRoute><PricingCatalogEditor /></VelaroAdminRoute>
      },
      {
        // Policy Notices — broadcast Terms/Privacy/AUP changes to every account's Account Owner
        path: "/super-admin/policy-notices",
        element: <VelaroAdminRoute><PolicyNotices /></VelaroAdminRoute>
      },
      {
        // Skill Framework v2 — SuperAdmin routing overrides editor (Phase 6b)
        path: "/admin/skill-routing-overrides",
        element: <SupportAdminRoute><SkillRoutingOverridesPage /></SupportAdminRoute>
      },
      {
        // Same page, pre-fill mode from "Tune this misroute" navigation
        path: "/admin/skill-routing-overrides/new",
        element: <SupportAdminRoute><SkillRoutingOverridesPage /></SupportAdminRoute>
      },
      {
        path: "/admin/database-tool",
        element: (
          <VelaroAdminRoute>
            <RouteHeader title="Database Administration">
              <DatabaseTool />
            </RouteHeader>
          </VelaroAdminRoute>
        )
      },
      {
        path: "/admin/hangfire",
        element: (
          <VelaroAdminRoute>
            <RouteHeader title="Hangfire Monitor">
              <HangfireMonitor />
            </RouteHeader>
          </VelaroAdminRoute>
        )
      },
      {
        path: "/admin/search-indexes",
        element: (
          <VelaroAdminRoute>
            <RouteHeader title="Search Index Management">
              <SearchIndexes />
            </RouteHeader>
          </VelaroAdminRoute>
        )
      },
      {
        path: "/admin/site-sync",
        element: (
          <VelaroAdminRoute>
            <RouteHeader title="Site Sync">
              <SiteSync />
            </RouteHeader>
          </VelaroAdminRoute>
        )
      },
      {
        path: "/admin/deployments",
        element: (
          <SupportAdminRoute>
            <RouteHeader title="Deployments">
              <Deployments />
            </RouteHeader>
          </SupportAdminRoute>
        )
      },
      {
        path: "/admin/subscription-settings",
        element: (
          <VelaroAdminRoute>
            <RouteHeader title="Subscription Settings">
              <SubscriptionSettings />
            </RouteHeader>
          </VelaroAdminRoute>
        )
      },
      {
        path: "/admin/kb-domains",
        element: (
          <VelaroAdminRoute>
            <RouteHeader title="KB Custom Domains">
              <KBDomains />
            </RouteHeader>
          </VelaroAdminRoute>
        )
      },
      {
        path: "/admin/app-marketplace",
        element: (
          <VelaroAdminRoute>
            <RouteHeader title="App Marketplace Credentials">
              <AppMarketplace />
            </RouteHeader>
          </VelaroAdminRoute>
        )
      },
      {
        path: "/admin/campaign-health",
        element: (
          <VelaroAdminRoute>
            <RouteHeader title="Campaign Health">
              <CampaignHealth />
            </RouteHeader>
          </VelaroAdminRoute>
        )
      },
      {
        path: "/admin/detection-report",
        element: (
          <VelaroAdminRoute>
            <RouteHeader title="Site Detection Report">
              <DetectionReport />
            </RouteHeader>
          </VelaroAdminRoute>
        )
      },
      {
        path: "/admin/copilot",
        element: (
          <VelaroAdminRoute>
            <RouteHeader title="AI Platform">
              <CopilotAdmin />
            </RouteHeader>
          </VelaroAdminRoute>
        )
      },
      {
        path: "/admin/pricing-quote",
        element: (
          <VelaroAdminRoute>
            <RouteHeader title="Pricing & Quote Builder">
              <PricingQuote />
            </RouteHeader>
          </VelaroAdminRoute>
        )
      },
      {
        path: "/admin/platform-setup",
        element: (
          <VelaroAdminRoute>
            <RouteHeader title="Platform Channel Setup">
              <PlatformChannelSetup />
            </RouteHeader>
          </VelaroAdminRoute>
        )
      },
      {
        path: "/admin/design-v2",
        element: (
          <VelaroAdminRoute>
            <RouteHeader title="Design System (2026)">
              <DesignSystemV2 />
            </RouteHeader>
          </VelaroAdminRoute>
        )
      },
      {
        path: "/admin/ivr-settings",
        element: (
          <VelaroAdminRoute>
            <RouteHeader title="IVR Platform Settings">
              <IvrPlatformSettings />
            </RouteHeader>
          </VelaroAdminRoute>
        )
      },
      /* DEAD — MF Athletic Demo removed from nav. Direct URL /admin/mf-athletic-demo no longer registered. Kept for 30s revival.
      {
        path: "/admin/mf-athletic-demo",
        element: (
          <VelaroAdminRoute>
            <RouteHeader title="MF Athletic Demo">
              <MFAthleticDemo />
            </RouteHeader>
          </VelaroAdminRoute>
        )
      }, */
      {
        path: "/admin/ai-prompt-policy",
        element: (
          <VelaroAdminRoute>
            <RouteHeader title="AI Prompt Policies">
              <AIPromptPolicy />
            </RouteHeader>
          </VelaroAdminRoute>
        )
      },
      {
        path: "/admin/live-monitor",
        element: <VelaroAdminRoute><LiveMonitor /></VelaroAdminRoute>
      },
      {
        path: "/admin/system-health",
        element: <VelaroAdminRoute><SystemHealth /></VelaroAdminRoute>
      },
      {
        path: "/admin/account-health",
        element: <VelaroAdminRoute><AccountHealth /></VelaroAdminRoute>
      },
      {
        path: "/admin/am-assignments",
        element: <VelaroAdminRoute><AccountManagerAssignments /></VelaroAdminRoute>
      },
      {
        path: "/admin/billing-overage",
        element: (
          <VelaroAdminRoute billing>
            <RouteHeader title="Billing Overage Settings">
              <BillingOverage />
            </RouteHeader>
          </VelaroAdminRoute>
        )
      },
      {
        path: "/admin/billing-financial",
        element: (
          <VelaroAdminRoute billing>
            <RouteHeader title="Financial Dashboard">
              <BillingFinancial />
            </RouteHeader>
          </VelaroAdminRoute>
        )
      },
      {
        path: "/admin/billing-staff",
        element: (
          <VelaroAdminRoute billing>
            <RouteHeader title="Billing Staff">
              <BillingStaff />
            </RouteHeader>
          </VelaroAdminRoute>
        )
      },
      {
        // Billing Admin Guide — always-available reference for the full billing system
        path: "/admin/billing-guide",
        element: <VelaroAdminRoute billing><BillingGuide /></VelaroAdminRoute>
      },
      {
        // Unified Plans & Pricing hub — consolidates Checkout Plans + Catalog Prices + Feature Packages
        path: "/admin/plans-pricing",
        element: <VelaroAdminRoute billing><PlansAndPricing /></VelaroAdminRoute>
      },
      {
        // New Customer Setup wizard — guided 4-step subscription onboarding
        path: "/admin/subscription-new",
        element: <VelaroAdminRoute billing><SubscriptionSetupWizard /></VelaroAdminRoute>
      },
      {
        // Legacy direct URL — kept so bookmarks don't 404
        path: "/admin/billing/plans",
        element: (
          <VelaroAdminRoute billing>
            <RouteHeader title="Plan Management">
              <BillingPlans />
            </RouteHeader>
          </VelaroAdminRoute>
        )
      },
      {
        path: "/admin/activity-log",
        element: (
          <VelaroAdminRoute>
            <RouteHeader title="Activity Log">
              <ActivityLog />
            </RouteHeader>
          </VelaroAdminRoute>
        )
      },
      {
        path: "/admin/velaro-logger",
        element: (
          <SupportAdminRoute>
            <RouteHeader title="Velaro Logger">
              <VelaroLogger />
            </RouteHeader>
          </SupportAdminRoute>
        )
      },
      {
        path: "/admin/agent-login-logs",
        element: (
          <VelaroAdminRoute>
            <RouteHeader title="Agent Login History">
              <AgentLoginLogs />
            </RouteHeader>
          </VelaroAdminRoute>
        )
      },
      {
        path: "/admin/support-tools",
        element: (
          <VelaroAdminRoute>
            <RouteHeader title="Support Tools">
              <SupportTools />
            </RouteHeader>
          </VelaroAdminRoute>
        )
      },
      {
        path: "/admin/tool-usage",
        element: (
          <VelaroAdminRoute>
            <RouteHeader title="Tool Usage Report">
              <ToolUsageReport />
            </RouteHeader>
          </VelaroAdminRoute>
        )
      },
      {
        path: "/settings/roles",
        element: (
          <StickyRouteHeader title="Roles & Permissions" subTitle="Control what each user role can access and do">
            <UserRoles />
          </StickyRouteHeader>
        )
      },
      {
        path: "/settings/ai-provider",
        element: (
          <StickyRouteHeader title="API Keys">
            <AIProviderSettings />
          </StickyRouteHeader>
        )
      },
      {
        path: "/settings/api-keys",
        element: (
          <StickyRouteHeader title="API Keys">
            <AIProviderSettings />
          </StickyRouteHeader>
        )
      },
      {
        path: "/settings/hub",
        element: <SettingsHub />
      },
      {
        path: "/settings/condition-presets",
        element: (
          <StickyRouteHeader title="Condition Presets" subTitle="Save reusable condition groups to use across rules, routing, and workflows">
            <ConditionPresetsPage />
          </StickyRouteHeader>
        )
      },
      {
        path: "/focus-inbox",
        element: (
          <StickyRouteHeader title="Focus Inbox">
            <FocusInbox />
          </StickyRouteHeader>
        )
      },
      {
        path: "/users/",
        element: <Users />
      },
      {
        path: "/users/:userId",
        element: (
          <RouteHeader
            title="User Profile"
            headerActions={<BackToUsersButton />}
          >
            <UserProfile />
          </RouteHeader>
        )
      },
      {
        path: "/user/add",
        element: (
          <RouteHeader title="Add User">
            <AddUserWizard />
          </RouteHeader>
        )
      },
      {
        path: "/contact_center/*",
        element: (
          <RouteHeader title="Contact Center">
            <ContactCenter />
          </RouteHeader>
        )
      },
      {
        path: "/contact-segments",
        element: <ContactSegments />
      },
      {
        path: "/teams",
        element: <Teams />
      },
      {
        path: "/teams-routing",
        element: <TeamsRouting />
      },
      {
        path: "/schedules",
        element: <Schedules />
      },
      {
        path: "/routing-ops",
        element: (
          <GatedEntitlementFeature flagKey="EnableRoutingOpsDashboard" name="Routing Ops Dashboard">
            <RoutingOpsDashboard />
          </GatedEntitlementFeature>
        )
      },
      {
        path: "/deployment",
        element: (
          <AgreementGate slug="deployment-compliance">
            <Deployment />
          </AgreementGate>
        )
      },
      {
        path: "/tags",
        element: (
          <StickyRouteHeader title="Tags" subTitle="Organize conversations and contacts with labels">
            <Tags />
          </StickyRouteHeader>
        )
      },
      {
        path: "/custom-fields",
        element: <CustomFields />
      },
      {
        path: "/facebook",
        element: (
          <StickyRouteHeader title="Facebook" subTitle="Connect Facebook Pages to receive and respond to Messenger conversations">
            <FacebookIntegration />
          </StickyRouteHeader>
        )
      },
      {
        path: "/instagram",
        element: (
          <StickyRouteHeader title="Instagram" subTitle="Connect Instagram accounts to manage DMs and comments from Velaro">
            <InstagramIntegration />
          </StickyRouteHeader>
        )
      },
      {
        path: "/tiktok",
        element: (
          <StickyRouteHeader title="TikTok" subTitle="Connect TikTok accounts to respond to comments and DMs">
            <TikTokIntegration />
          </StickyRouteHeader>
        )
      },
      {
        path: "/tiktok/callback",
        element: <TikTokCallback />
      },
      {
        path: "/sms",
        element: (
          <StickyRouteHeader title="SMS" subTitle="Configure SMS channels to send and receive text messages">
            <SmsIntegration />
          </StickyRouteHeader>
        )
      },
      {
        path: "/whatsapp",
        element: (
          <StickyRouteHeader title="WhatsApp" subTitle="Connect WhatsApp Business accounts to chat with customers">
            <WhatsAppIntegration />
          </StickyRouteHeader>
        )
      },
      {
        path: "/rcs",
        element: (
          <StickyRouteHeader title="RCS" subTitle="Enable Rich Communication Services for enhanced mobile messaging">
            <RcsIntegration />
          </StickyRouteHeader>
        )
      },
      {
        path: "/apple-messages",
        element: (
          <RouteHeader title="Apple Messages for Business">
            <div className="bg-white border border-slate-200 rounded-xl p-5 sm:p-6 overflow-hidden min-w-0">
              <AppleMessagesIntegration />
            </div>
          </RouteHeader>
        )
      },
      {
        path: "/ivr",
        element: (
          <StickyRouteHeader title="IVR" subTitle="Configure your AI-powered phone channel" headerActions={<AddPhoneNumberButton />}>
            <IvrIntegration hidePageTitle />
          </StickyRouteHeader>
        )
      },
      {
        path: "/click-to-call",
        element: <ClickToCallPage />
      },
      {
        path: "/avatar",
        element: (
          <StickyRouteHeader title="AI Video Avatars" subTitle="Configure AI-powered video avatar personas for your chat channels">
            <AvatarSettings />
          </StickyRouteHeader>
        )
      },
      {
        path: "/surveys",
        element: <Surveys />
      },
      {
        path: "/chat-experience",
        element: (
          <RouteHeader title="Chat Experience">
            <ChatExperience />
          </RouteHeader>
        )
      },
      {
        path: "/integrations",
        element: (
          <StickyRouteHeader
            title="Integrations"
            subTitle="Configure integrations to fit your specific business needs, enhancing your ability to manage everything in one place."
          >
            <Landing />
          </StickyRouteHeader>
        )
      },
      {
        path: "/twitter/callback",
        element: <TwitterCallback />
      },
      {
        path: "/netsuite/oauthcallback",
        element: <NetSuiteCallback />
      },
      {
        path: "/netsuite/oauth2callback",
        element: <NetSuiteOAuth2Callback />
      },
      {
        path: "/auto_responses",
        element: (
          <RouteHeader title="Auto Responses">
            <AutoResponses />
          </RouteHeader>
        )
      },
      {
        path: "/canned_responses",
        element: (
          <StickyRouteHeader title="Canned Responses" subTitle="Create reusable message templates for faster, consistent agent replies">
            <CannedResponses />
          </StickyRouteHeader>
        )
      },
      /* DEAD — replaced by Chat Window Designer (/chat_window) + Rules engine. Kept for 30d revival.
      {
        path: "/web_messenger",
        element: (
          <RouteHeader title="Web Messenger">
            <WebMessenger />
          </RouteHeader>
        )
      }, */
      {
        path: "/chat_window",
        element: (
          <StickyRouteHeader
            title="Chat Window"
            subTitle="Design and manage your chat widgets"
            headerActions={<CreateChatWindowButton />}
          >
            <ChatWindow />
          </StickyRouteHeader>
        )
      },
      {
        path: "/chat_window/:chatWindowId",
        element: <ChatWindowEditor />
      },
      {
        path: "/work_schedules",
        element: (
          <RouteHeader title="Work Schedules">
            <WorkSchedules />
          </RouteHeader>
        )
      },
      {
        path: "/integrations/netsuite",
        element: (
          <GatedSubscriptionFeature flagKey="enableNetsuite" name="NetSuite CRM" meta={INTEGRATION_META.enableNetsuite}>
            <NetSuite />
          </GatedSubscriptionFeature>
        )
      },
      {
        path: "/integrations/netsuite/netsuitecreate",
        element: (
          <RouteHeader title="">
            <NetSuiteCreate />
          </RouteHeader>
        )
      },
      {
        path: "/integrations/formstack",
        element: (
          <GatedSubscriptionFeature flagKey="enableFormstack" name="Formstack" meta={INTEGRATION_META.enableFormstack}>
            <Formstack />
          </GatedSubscriptionFeature>
        )
      },
      {
        path: "/integrations/formstack/formstackcreate",
        element: (
          <RouteHeader title="">
            <FormstackCreate />
          </RouteHeader>
        )
      },
      {
        path: "/integrations/accela-civic",
        element: (
          <GatedSubscriptionFeature flagKey="enableAccela" name="Accela Civic" meta={INTEGRATION_META.enableAccela}>
            <AccelaCivic />
          </GatedSubscriptionFeature>
        )
      },
      {
        path: "/integrations/accela-civic/AccelaCreate",
        element: (
          <RouteHeader title="Accela">
            <AccelaCivicCreate />
          </RouteHeader>
        )
      },
      {
        path: "/integrations/accela-civic/AccelaCallbackBridge",
        element: (
          <RouteHeader title="Accela">
            <AccelaCallbackBridge />
          </RouteHeader>
        )
      },
      {
        // Post-install landing page — merchants land here after Shopify App Store
        // install + OAuth. Shows widget activation deep-link for Theme App Extension.
        // No RouteHeader: full-screen onboarding layout.
        path: "/shopify/install",
        element: <ShopifyInstall />
      },
      {
        path: "/integrations/Shopify",
        element: (
          <GatedAdminFeature flagKey="enableShopify" name="Shopify" meta={INTEGRATION_META.enableShopify}>
            <Shopify />
          </GatedAdminFeature>
        )
      },
      {
        path: "/integrations/shopify/ShopifyCreate",
        element: (
          <RouteHeader
            title=""
            headerActions={<ShopifyBackToShopifyOverview />}
          >
            <ShopifyCreate />
          </RouteHeader>
        )
      },
      {
        path: "/integrations/shopify/ShopifyOverview",
        element: (
          <RouteHeader
            title=""
            headerActions={<ShopifyBackToIntegrationLanding />}
          >
            <ShopifyOverview />
          </RouteHeader>
        )
      },
      {
        path: "/integrations/log",
        element: <IntegrationLog />
      },
      {
        path: "/integrations/mcp",
        element: <McpServers />
      },
      {
        path: "/integrations/copilot-studio",
        element: <CopilotStudioPage />
      },
      {
        // Not yet listed on Microsoft AppSource (see docs/microsoft-partner-center-checklist.md
        // Step D/E) — gate to Velaro staff only until the ISV listing is live and customers can
        // actually install the add-in. Remove this gate once that checklist is complete.
        path: "/integrations/copilot-for-sales",
        element: <VelaroAdminRoute><CopilotForSalesPage /></VelaroAdminRoute>
      },
      {
        path: "/integrations/payments/stripe",
        element: <StripePayments />
      },
      {
        path: "/integrations/hubspot",
        element: (
          <GatedAdminFeature flagKey="enableHubSpot" name="HubSpot CRM" meta={INTEGRATION_META.enableHubSpot}>
            <HubSpot />
          </GatedAdminFeature>
        )
      },
      {
        path: "/integrations/hubspot/overview",
        element: (
          <RouteHeader title="" headerActions={<HubSpotBackToIntegrationLanding />}>
            <HubSpotOverview />
          </RouteHeader>
        )
      },
      {
        path: "/integrations/hubspot/create",
        element: (
          <RouteHeader title="" headerActions={<HubSpotBackToHubSpotOverview />}>
            <HubSpotCreate />
          </RouteHeader>
        )
      },
      {
        path: "/integrations/bigcommerce",
        element: (
          <GatedAdminFeature flagKey="enableBigCommerce" name="BigCommerce" meta={INTEGRATION_META.enableBigCommerce}>
            <BigCommerce />
          </GatedAdminFeature>
        )
      },
      {
        path: "/integrations/bigcommerce/overview",
        element: (
          <RouteHeader title="" headerActions={<BigCommerceBackToIntegrationLanding />}>
            <BigCommerceOverview />
          </RouteHeader>
        )
      },
      {
        path: "/integrations/bigcommerce/create",
        element: (
          <RouteHeader title="" headerActions={<BigCommerceBackToOverview />}>
            <BigCommerceCreate />
          </RouteHeader>
        )
      },
      {
        path: "/integrations/magento",
        element: (
          <GatedAdminFeature flagKey="enableMagento" name="Magento / Adobe Commerce" meta={INTEGRATION_META.enableMagento}>
            <Magento />
          </GatedAdminFeature>
        )
      },
      {
        path: "/integrations/magento/overview",
        element: (
          <RouteHeader title="" headerActions={<MagentoBackToIntegrationLanding />}>
            <MagentoOverview />
          </RouteHeader>
        )
      },
      {
        path: "/integrations/magento/create",
        element: (
          <RouteHeader title="" headerActions={<MagentoBackToOverview />}>
            <MagentoCreate />
          </RouteHeader>
        )
      },
      {
        path: "/integrations/woocommerce",
        element: (
          <GatedAdminFeature flagKey="enableWooCommerce" name="WooCommerce" meta={INTEGRATION_META.enableWooCommerce}>
            <WooCommerce />
          </GatedAdminFeature>
        )
      },
      {
        path: "/integrations/dynamics",
        element: (
          <GatedAdminFeature flagKey="enableDynamics" name="Microsoft Dynamics 365" meta={INTEGRATION_META.enableDynamics}>
            <Dynamics />
          </GatedAdminFeature>
        )
      },
      {
        path: "/integrations/dynamics/overview",
        element: (
          <RouteHeader title="" headerActions={<DynamicsBackToIntegrationLanding />}>
            <DynamicsOverview />
          </RouteHeader>
        )
      },
      {
        path: "/integrations/dynamics/create",
        element: (
          <RouteHeader title="" headerActions={<DynamicsBackToOverview />}>
            <DynamicsCreate />
          </RouteHeader>
        )
      },
      {
        path: "/integrations/zendesk/overview",
        element: (
          <RouteHeader title="" headerActions={<ZendeskBackToIntegrationLanding />}>
            <ZendeskOverview />
          </RouteHeader>
        )
      },
      {
        path: "/integrations/velaro-crm",
        element: <NativeCrm />
      },
      {
        path: "/integrations/salesforce",
        element: (
          <GatedAdminFeature flagKey="enableSalesforce" name="Salesforce CRM" meta={INTEGRATION_META.enableSalesforce}>
            <Salesforce />
          </GatedAdminFeature>
        )
      },
      {
        path: "/integrations/servicenow",
        element: <ServiceNow />
      },
      {
        path: "/integrations/servicenow/create",
        element: (
          <RouteHeader title="Connect ServiceNow" headerActions={<BackToIntegrationLandingServiceNow />}>
            <ServiceNowCreate />
          </RouteHeader>
        )
      },
      {
        path: "/integrations/sap",
        element: <SAP />
      },
      {
        path: "/integrations/sap/create",
        element: (
          <RouteHeader title="Connect SAP" headerActions={<BackToIntegrations />}>
            <SapCreate />
          </RouteHeader>
        )
      },
      {
        path: "/integrations/uplisting",
        element: (
          <GatedAdminFeature flagKey="enableUplisting" name="Uplisting" meta={INTEGRATION_META.enableUplisting}>
            <Uplisting />
          </GatedAdminFeature>
        )
      },
      {
        path: "/integrations/uplisting/create",
        element: (
          <RouteHeader title="Connect Uplisting" headerActions={<BackToIntegrationLandingUplisting />}>
            <UplistingCreate />
          </RouteHeader>
        )
      },
      {
        path: "/integrations/guesty",
        element: (
          <GatedAdminFeature flagKey="enableGuesty" name="Guesty" meta={INTEGRATION_META.enableGuesty}>
            <Guesty />
          </GatedAdminFeature>
        )
      },
      {
        path: "/integrations/guesty/create",
        element: (
          <RouteHeader title="Connect Guesty" headerActions={<BackToIntegrationLandingGuesty />}>
            <GuestyCreate />
          </RouteHeader>
        )
      },
      {
        path: "/integrations/cloudbeds",
        element: (
          <GatedAdminFeature flagKey="enableCloudbeds" name="Cloudbeds" meta={INTEGRATION_META.enableCloudbeds}>
            <Cloudbeds />
          </GatedAdminFeature>
        )
      },
      {
        path: "/integrations/cloudbeds/create",
        element: (
          <RouteHeader title="Connect Cloudbeds" headerActions={<BackToIntegrationLandingCloudbeds />}>
            <CloudbedsCreate />
          </RouteHeader>
        )
      },
      {
        path: "/integrations/pipedrive",
        element: (
          <GatedAdminFeature flagKey="enablePipedrive" name="Pipedrive" meta={INTEGRATION_META.enablePipedrive}>
            <Pipedrive />
          </GatedAdminFeature>
        )
      },
      {
        path: "/integrations/quickbooks",
        element: (
          <GatedAdminFeature flagKey="enableQuickBooks" name="QuickBooks Online" meta={INTEGRATION_META.enableQuickBooks}>
            <QuickBooks />
          </GatedAdminFeature>
        )
      },
      {
        path: "/integrations/zoho",
        element: (
          <GatedAdminFeature flagKey="enableZoho" name="Zoho CRM" meta={INTEGRATION_META.enableZoho}>
            <Zoho />
          </GatedAdminFeature>
        )
      },
      {
        path: "/integrations/toast",
        element: (
          <GatedAdminFeature flagKey="enableToast" name="Toast POS" meta={INTEGRATION_META.enableToast}>
            <Toast />
          </GatedAdminFeature>
        )
      },
      {
        path: "/integrations/clover",
        element: (
          <GatedSubscriptionFeature flagKey="enableCloverSkills" name="Clover POS" meta={INTEGRATION_META.enableCloverSkills}>
            <Clover />
          </GatedSubscriptionFeature>
        )
      },
      {
        path: "/integrations/mindbody",
        element: (
          <GatedAdminFeature flagKey="enableMindbody" name="Mindbody" meta={INTEGRATION_META.enableMindbody}>
            <Mindbody />
          </GatedAdminFeature>
        )
      },
      {
        path: "/integrations/mindbody/create",
        element: (
          <RouteHeader title="Connect Mindbody" headerActions={<BackToIntegrationLandingMindbody />}>
            <GatedAdminFeature flagKey="enableMindbody" name="Mindbody" meta={INTEGRATION_META.enableMindbody}>
              <MindbodyCreate />
            </GatedAdminFeature>
          </RouteHeader>
        )
      },
      {
        path: "/integrations/monday",
        element: (
          <GatedAdminFeature flagKey="enableMonday" name="Monday.com" meta={INTEGRATION_META.enableMonday}>
            <Monday />
          </GatedAdminFeature>
        )
      },
      {
        path: "/integrations/recruiting",
        element: (
          <GatedSubscriptionFeature flagKey="enableRecruitingBot" name="Recruiting Bot" meta={INTEGRATION_META.enableRecruitingBot}>
            <Recruiting />
          </GatedSubscriptionFeature>
        )
      },
      {
        path: "/integrations/jobber",
        element: (
          <GatedAdminFeature flagKey="enableJobber" name="Jobber" meta={INTEGRATION_META.enableJobber}>
            <Jobber />
          </GatedAdminFeature>
        )
      },
      {
        path: "/integrations/callrail",
        element: (
          <GatedAdminFeature flagKey="enableCallRail" name="CallRail Attribution" meta={INTEGRATION_META.enableCallRail}>
            <CallRail />
          </GatedAdminFeature>
        )
      },
      {
        path: "/integrations/servicetitan",
        element: (
          <GatedAdminFeature flagKey="enableServiceTitan" name="ServiceTitan" meta={INTEGRATION_META.enableServiceTitan}>
            <ServiceTitanPage />
          </GatedAdminFeature>
        )
      },
      {
        path: "/integrations/airtable",
        element: (
          <GatedAdminFeature flagKey="enableAirtable" name="Airtable" meta={INTEGRATION_META.enableAirtable}>
            <AirtablePage />
          </GatedAdminFeature>
        )
      },
      {
        path: "/integrations/notion",
        element: (
          <GatedAdminFeature flagKey="enableNotion" name="Notion" meta={INTEGRATION_META.enableNotion}>
            <NotionPage />
          </GatedAdminFeature>
        )
      },
      {
        path: "/integrations/power-automate",
        element: <PowerAutomate />
      },
      {
        path: "/integrations/make",
        element: <MakeIntegration />
      },
      {
        path: "/integrations/zapier",
        element: <ZapierIntegration />
      },
      {
        path: "/integrations/transcript-storage",
        element: <TranscriptStorage />
      },
      {
        path: "/integrations/microsoft-365",
        element: <Microsoft365 />
      },
      {
        path: "/integrations/microsoft-bookings",
        element: (
          <GatedSubscriptionFeature flagKey="enableMicrosoftBookingsSkills" name="Microsoft Bookings" meta={INTEGRATION_META.enableMicrosoftBookingsSkills}>
            <MicrosoftBookings />
          </GatedSubscriptionFeature>
        )
      },
      {
        path: "/integrations/opentable",
        element: <OpenTableIntegration />
      },
      {
        path: "/integrations/resy",
        element: <ResyIntegration />
      },
      {
        path: "/integrations/microsoft-365/teams-notifications",
        element: <TeamsNotifications />
      },
      {
        path: "/integrations/microsoft-365/onedrive",
        element: <OneDriveSettingsPage />
      },
      {
        path: "/integrations/teams-notifications",
        element: <TeamsNotifications />
      },
      {
        path: "/integrations/teams-channel",
        element: <TeamsChannel />
      },
      {
        path: "/integrations/slack-notifications",
        element: <SlackNotificationsPage />
      },
      {
        path: "/integrations/alert-rules",
        element: <AlertRulesPage />
      },
      {
        path: "/integrations/gmail-inbox",
        element: <GmailInbox />
      },
      {
        path: "/integrations/outlook-inbox",
        element: <OutlookInbox />
      },
      {
        path: "/integrations/outlook-calendar",
        element: <OutlookCalendar />
      },
      {
        path: "/integrations/calendly",
        element: <CalendlyIntegration />
      },
      {
        path: "/integrations/custom-commerce-connector",
        element: (
          <RouteHeader title="Custom Commerce Connector" headerActions={<BackToIntegrations />}>
            <GatedEntitlementFeature flagKey="EnableCustomCommerceConnector" name="Custom Commerce Connector">
              <CustomCommerceIntegration />
            </GatedEntitlementFeature>
          </RouteHeader>
        )
      },
      {
        path: "/integrations/callback-scheduling",
        element: (
          <RouteHeader title="Callback Scheduling" headerActions={<BackToIntegrations />}>
            <GatedEntitlementFeature
              flagKey="EnableCallbackScheduling"
              name="Callback Scheduling"
              meta={INTEGRATION_META.EnableCallbackScheduling}
            >
              <CallbackSchedulingSetupWizard />
            </GatedEntitlementFeature>
          </RouteHeader>
        )
      },
      {
        path: "/integrations/callback-scheduling/reporting",
        element: (
          <RouteHeader title="Callback Scheduling — Report" headerActions={<BackToIntegrations />}>
            <GatedEntitlementFeature
              flagKey="EnableCallbackScheduling"
              name="Callback Scheduling"
              meta={INTEGRATION_META.EnableCallbackScheduling}
            >
              <CallbackSchedulingReport />
            </GatedEntitlementFeature>
          </RouteHeader>
        )
      },
      {
        path: "/integrations/acuity",
        element: <AcuityIntegration />
      },
      {
        path: "/integrations/square-appointments",
        element: <SquareIntegration />
      },
      {
        // OAuth2 callback landing page — Square redirects here with ?code=...&state=...
        path: "/integrations/square/callback",
        element: <SquareCallbackPage />
      },
      {
        path: "/integrations/klaviyo",
        element: <KlaviyoIntegration />
      },
      {
        path: "/integrations/vast",
        element: (
          <GatedAdminFeature flagKey="enableVast" name="VAST Retail" meta={INTEGRATION_META.enableVast}>
            <Vast />
          </GatedAdminFeature>
        )
      },
      {
        path: "/integrations/nextiva",
        element: (
          <GatedAdminFeature flagKey="enableNextiva" name="Nextiva" meta={INTEGRATION_META.enableNextiva}>
            <NextivaPage />
          </GatedAdminFeature>
        )
      },
      {
        path: "/integrations/teams-phone",
        element: (
          <GatedEntitlementFeature flagKey="TeamsPhone" name="Microsoft Teams Phone" meta={INTEGRATION_META.TeamsPhone}>
            <TeamsPhonePage />
          </GatedEntitlementFeature>
        )
      },
      {
        path: "/integrations/skyswitch",
        element: (
          <GatedEntitlementFeature flagKey="EnableVelaroPhone" name="Velaro Voice" meta={INTEGRATION_META.EnableVelaroPhone}>
            <SkySwitchPage />
          </GatedEntitlementFeature>
        )
      },
      {
        path: "/integrations/secure-forms",
        element: (
          <GatedAdminFeature flagKey="enableSecureForms" name="SecureForms" meta={INTEGRATION_META.enableSecureForms}>
            <SecureFormsPanel />
          </GatedAdminFeature>
        )
      },
      {
        path: "/integrations/webflow",
        element: (
          <GatedAdminFeature flagKey="enableWebflow" name="Webflow" meta={INTEGRATION_META.enableWebflow}>
            <Webflow />
          </GatedAdminFeature>
        )
      },
      {
        path: "/integrations/webflow/connect",
        element: (
          <RouteHeader title="Connect Webflow Site" headerActions={<BackToIntegrations />}>
            <WebflowConnect />
          </RouteHeader>
        )
      },
      {
        path: "/integrations/gorgias",
        element: <GorgiasWizard />
      },
      {
        path: "/integrations/google-tag-manager",
        element: <GoogleTagManager />
      },
      {
        path: "/integrations/ga4",
        element: <GA4Analytics />
      },
      {
        // "Build Your Bot" 5-step wizard — new entry point, does not touch existing /bots routes
        path: "/chatbots/new",
        element: <BuildBotWizard />
      },
      {
        // Language & Translation setup wizard — additive, does not override existing config
        path: "/language-setup",
        element: <LanguageSetupWizard />
      },
      {
        path: "/bots",
        element: (
          <StickyRouteHeader title="Bot Personas" subTitle="Create and manage AI bot personalities">
            <BotManager />
          </StickyRouteHeader>
        )
      },
      {
        path: "/bots/training/index",
        element: (
          <StickyRouteHeader
            title="AI Training"
            headerActions={<CreateIndexButton></CreateIndexButton>}
            tabs={<AIChatbotTrainingSettingsTabs selectedTab="Index" />}
          >
            <AIChatbotTrainingIndex />
          </StickyRouteHeader>
        )
      },
      {
        path: "/bots/training/web",
        element: (
          <StickyRouteHeader title="AI Training" headerActions={null} tabs={<AIChatbotTrainingSettingsTabs selectedTab="Web Training" />}>
            <AIChatbotWebTraining />
          </StickyRouteHeader>
        )
      },
      {
        path: "/bots/training/file",
        element: (
          <StickyRouteHeader title="AI Training" headerActions={null} tabs={<AIChatbotTrainingSettingsTabs selectedTab="File Training" />}>
            <AIChatbotFileTraining />
          </StickyRouteHeader>
        )
      },
      // /ai-chatbot/* → redirect to /ai-new/*
      { path: "/ai-chatbot/ai-configuration", element: <Navigate to="/ai-new/settings" replace /> },
      { path: "/ai-chatbot/ai-configuration/:id", element: <Navigate to="/ai-new/settings" replace /> },
      { path: "/ai-chatbot/*", element: <Navigate to="/ai-new/settings" replace /> },
      // /ai/* → redirect to /ai-new/* (all old chatbot URLs forward to the new UI)
      { path: "/ai", element: <Navigate to="/ai-new/settings" replace /> },
      { path: "/ai/settings/general/:id", element: <RedirectAIToNew suffix="general" /> },
      { path: "/ai/settings/training/:id", element: <RedirectAIToNew suffix="general" /> },
      { path: "/ai/settings/handoff/:id", element: <RedirectAIToNew suffix="handoff" /> },
      { path: "/ai/settings/playground/:id", element: <RedirectAIToNew suffix="test-chat" /> },
      { path: "/ai/settings/knowledge-overrides/:id", element: <RedirectAIToNew suffix="knowledge-overrides" /> },
      { path: "/ai/settings/suppressed-content/:id", element: <RedirectAIToNew suffix="suppressed-content" /> },
      { path: "/ai/settings/qna/:id", element: <RedirectAIToNew suffix="qna" /> },
      { path: "/ai/settings/skills/:id", element: <RedirectAIToNew suffix="skills" /> },
      { path: "/ai/settings/test/:id", element: <RedirectAIToNew suffix="test-chat" /> },
      // Named redirects for known external links / bookmarks pointing at old paths
      { path: "/ai/knowledge-indexes", element: <Navigate to="/ai-new/indexes" replace /> },
      { path: "/ai/indexes", element: <Navigate to="/ai-new/indexes" replace /> },
      // {
      //   path: "/ai/settings/data/:id",
      //   element: (
      //     <StickyRouteHeader
      //       title="AI Chatbot"
      //       headerActions={<SaveChatbotButton></SaveChatbotButton>}
      //       backLink="/ai"
      //     >
      //       <AIChatbotSettingsData />
      //     </StickyRouteHeader>
      //   )
      // },
      // {
      //   path: "/ai/settings/questions/:id",
      //   element: (
      //     <StickyRouteHeader
      //       title="AI Chatbot"
      //       headerActions={<SaveChatbotButton></SaveChatbotButton>}
      //       backLink="/ai"
      //     >
      //       <AIChatbotSettingsStarterQuestions />
      //     </StickyRouteHeader>
      //   )
      // },
      {
        path: "/ai-new/settings",
        element: (
          <StickyRouteHeader
            title="AI Configurations"
            headerActions={<AINewCreateChatbotButton />}
          >
            <AINewSettings />
          </StickyRouteHeader>
        )
      },
      {
        path: "/ai-new/settings/general/:id",
        element: (
          <StickyRouteHeader
            title="AI Configuration"
            headerActions={<AINewSaveChatbotButton />}
            backLink="/ai-new/settings"
            tabs={<AIConfigSettingsTabs />}
          >
            <AINewSettingsGeneral />
          </StickyRouteHeader>
        )
      },
      {
        path: "/ai-new/settings/persona/:id",
        element: (
          <StickyRouteHeader
            title="AI Configuration"
            backLink="/ai-new/settings"
            tabs={<AIConfigSettingsTabs />}
          >
            <AINewSettingsPersona />
          </StickyRouteHeader>
        )
      },
      {
        path: "/ai-new/settings/knowledge/:id",
        element: (
          <StickyRouteHeader
            title="AI Configuration"
            headerActions={<AINewSaveChatbotButton />}
            backLink="/ai-new/settings"
            tabs={<AIConfigSettingsTabs />}
          >
            <AINewSettingsKnowledge />
          </StickyRouteHeader>
        )
      },
      {
        path: "/ai-new/settings/handoff/:id",
        element: (
          <StickyRouteHeader
            title="AI Configuration"
            headerActions={<AINewSaveChatbotButton />}
            backLink="/ai-new/settings"
            tabs={<AIConfigSettingsTabs />}
          >
            <AINewSettingsHandoff />
          </StickyRouteHeader>
        )
      },
      {
        path: "/ai-new/settings/test-chat/:id",
        element: (
          <StickyRouteHeader
            title="AI Configuration"
            headerActions={<AINewSaveChatbotButton />}
            backLink="/ai-new/settings"
            tabs={<AIConfigSettingsTabs />}
          >
            <AINewSettingsPlayground />
          </StickyRouteHeader>
        )
      },
      // Legacy playground path → redirect to test-chat
      { path: "/ai-new/settings/playground/:id", element: <RedirectAIToNew suffix="test-chat" /> },
      // DEAD 2026-05-25 — TestIndexesPage was a thin wrapper around AINewSettingsPlayground
      // with a config picker. The Playground now has config + indexes pickers built in, so
      // a standalone wrapper is redundant. We keep the `/ai-new/playground/:id` route as the
      // new canonical standalone surface (no settings tabs chrome).
      // {
      //   path: "/ai-new/test-indexes",
      //   element: (<StickyRouteHeader title="Test Index"><TestIndexesPage /></StickyRouteHeader>),
      // },
      // {
      //   path: "/ai-new/test-indexes/:id",
      //   element: (<StickyRouteHeader title="Test Index" backLink="/ai-new/test-indexes"><TestIndexesPage /></StickyRouteHeader>),
      // },
      // Redirect the old route to the new standalone playground.
      { path: "/ai-new/test-indexes", element: <Navigate to="/ai-new/playground" replace /> },
      { path: "/ai-new/test-indexes/:id", element: <RedirectToPlayground /> },
      {
        path: "/ai-new/playground",
        element: (
          <StickyRouteHeader title="Playground" backLink="/ai-new/settings">
            <AINewSettingsPlayground />
          </StickyRouteHeader>
        ),
      },
      {
        path: "/ai-new/playground/:id",
        element: (
          <StickyRouteHeader title="Playground" backLink="/ai-new/settings">
            <AINewSettingsPlayground />
          </StickyRouteHeader>
        ),
      },
      {
        path: "/ai-new/settings/knowledge-overrides/:id",
        element: (
          <StickyRouteHeader
            title="AI Configuration"
            headerActions={<AINewSaveChatbotButton />}
            backLink="/ai-new/settings"
            tabs={<AIConfigSettingsTabs />}
          >
            <AINewSettingsKnowledgeOverrides />
          </StickyRouteHeader>
        )
      },
      {
        path: "/ai-new/settings/suppressed-content/:id",
        element: (
          <StickyRouteHeader
            title="AI Configuration"
            backLink="/ai-new/settings"
            tabs={<AIConfigSettingsTabs />}
          >
            <AIChatbotSettingsSuppressedContent />
          </StickyRouteHeader>
        )
      },
      {
        path: "/ai-new/settings/skills/:id",
        element: (
          <StickyRouteHeader
            title="AI Configuration"
            backLink="/ai-new/settings"
            tabs={<AIConfigSettingsTabs />}
          >
            <AINewSettingsSkills />
          </StickyRouteHeader>
        )
      },
      {
        path: "/ai-new/settings/channel-overrides/:id",
        element: (
          <StickyRouteHeader
            title="AI Configuration"
            headerActions={<AINewSaveChatbotButton />}
            backLink="/ai-new/settings"
            tabs={<AIConfigSettingsTabs />}
          >
            <AINewSettingsChannelOverrides />
          </StickyRouteHeader>
        )
      },
      {
        path: "/ai-new/settings/qna/:id",
        element: (
          <StickyRouteHeader
            title="AI Configuration"
            headerActions={<AINewSaveChatbotButton />}
            backLink="/ai-new/settings"
            tabs={<AIConfigSettingsTabs />}
          >
            <AINewSettingsQnA />
          </StickyRouteHeader>
        )
      },
      {
        path: "/ai-new/training",
        element: <AINewTraining />
      },
      {
        path: "/ai-new/chat",
        element: <Navigate to="/ai-new/settings" replace />
      },
      {
        path: "/ai-new/indexes",
        element: (
          <StickyRouteHeader title="AI Indexes">
            <AINewIndexes />
          </StickyRouteHeader>
        )
      },
      // /ai-new/indexes/:name links come from the dashboard — redirect to the indexes list
      { path: "/ai-new/indexes/:name", element: <Navigate to="/ai-new/indexes" replace /> },
      {
        path: "/rules",
        element: (
          <StickyRouteHeader title="Rules" subTitle="Automate conversation handling with conditional routing and escalation rules">
            <RuleManager />
          </StickyRouteHeader>
        )
      },
      {
        path: "/routing-history",
        element: (
          <StickyRouteHeader title="Routing History" subTitle="Audit trail of routing decisions applied to incoming conversations">
            <RoutingHistory />
          </StickyRouteHeader>
        )
      },
      {
        path: "/routing/settings",
        element: <RoutingSettings />
      },
      {
        path: "/routing/diagnostics",
        element: <RoutingDiagnosticsPage />
      },
      {
        path: "/routing/activity",
        element: <RoutingActivity />
      },
      {
        path: "/whats-running",
        element: (
          <StickyRouteHeader title="Activity" subTitle="Live view of active conversations, agents, and queue status — or look up a past conversation's routing decision.">
            <WhatsRunning />
          </StickyRouteHeader>
        )
      },
      {
        path: "/chat-preview",
        element: <ChatPreview />
      },
      {
        path: "/files",
        element: (
          <StickyRouteHeader title="File Upload" subTitle="Manage files that agents and visitors can share in conversations">
            <FileStorage />
          </StickyRouteHeader>
        )
      },
      {
        path: "/workflows/create",
        element: <WorkflowsCreate />
      },
      {
        path: "/workflows",
        element: <WorkflowsLandingGate />
      },
      {
        path: "/workflows/variable-guide",
        element: <VariableGuide />
      },
      {
        path: "/workflows/:id",
        element: <WorkflowsEditor />
      },
      {
        path: "/invitations",
        element: <Invitations />
      },
      {
        path: "/skills",
        element: (
          <StickyRouteHeader title="Skills" subTitle="Define routing tags used to match conversations to specialized agents">
            <Skills />
          </StickyRouteHeader>
        )
      },

      { path: "/kb", element: <Navigate to="/kb/articles" replace /> },
      {
        path: "/kb/articles",
        element: (
          <StickyRouteHeader title="Articles">
            <ArticlesIndex />
          </StickyRouteHeader>
        )
      },
      {
        path: "/kb/settings",
        element: (
          <StickyRouteHeader title="KB Settings" subTitle="Configure your knowledge base domain, branding, and access settings">
            <SettingsIndex />
          </StickyRouteHeader>
        )
      },
      {
        path: "/kb/articles/create",
        element: (
          <RouteHeader title="Create KB Article">
            <ArticlesCreate />
          </RouteHeader>
        )
      },
      //todo this id needs fixed
      {
        path: "/kb/articles/edit/:id",
        element: (
          <RouteHeader title="Edit KB Article">
            <ArticlesEdit />
          </RouteHeader>
        )
      },
      {
        path: "/kb/topics",
        element: (
          <StickyRouteHeader title="Topics" subTitle="Organize knowledge base articles into categories and topics">
            <TopicsIndex />
          </StickyRouteHeader>
        )
      },
      {
        path: "/kb/map",
        element: <SiteMap />
      },
      {
        path: "/kb/setup",
        element: (
          <RouteHeader title="KB Setup">
            <SetupIndex />
          </RouteHeader>
        )
      },
      {
        path: "/kb/performance",
        element: <KBPerformance />
      },
      {
        path: "/kb/widget",
        element: (
          <RouteHeader title="Search Widget">
            <KBWidgetEmbedCode />
          </RouteHeader>
        )
      },
      {
        path: "/kb/assets",
        element: (
          <StickyRouteHeader title="Asset Library">
            <AssetsIndex />
          </StickyRouteHeader>
        )
      },
      {
        path: "/kb/migrate",
        element: (
          <StickyRouteHeader title="Import" subTitle="Import content from another platform into your knowledge base">
            <KBMigrate />
          </StickyRouteHeader>
        )
      },
      {
        path: "/messaging/setup",
        element: <MessagingSetup />
      },
      {
        path: "/settings",
        element: <MessagingSettings />
      },
      // Legacy /copilot URL — Moshky is a drawer (FAB), not a page. Open it via
      // the global custom event and bounce back to home so the URL doesn't 404.
      {
        path: "/copilot",
        element: <OpenMoshkyRedirect />
      },
      // Friendly deep-link aliases used by the copilot — redirect to /settings?tab=...
      {
        path: "/settings/developer",
        element: <Navigate to="/settings?tab=apiKeys" replace />
      },
      {
        path: "/settings/security",
        element: <Navigate to="/settings?tab=security" replace />
      },
      {
        path: "/billing",
        element: <BillingPage />
      },
      // ── Velaro-internal billing pages (our own Stripe/QuickBooks ledger,
      // contracts, invoices we send to customers). Hidden from non-Velaro
      // identities even by direct URL. `/billing` (Usage & Plan) stays open
      // — that one is the customer's own subscription overview.
      {
        path: "/billing/contracts",
        element: (
          <VelaroAdminRoute billing>
            <RouteHeader title="Contracts">
              <Contracts />
            </RouteHeader>
          </VelaroAdminRoute>
        )
      },
      {
        path: "/billing/contracts/templates",
        element: (
          <VelaroAdminRoute billing>
            <RouteHeader title="Contract Templates">
              <ContractTemplates />
            </RouteHeader>
          </VelaroAdminRoute>
        )
      },
      {
        path: "/billing/invoices",
        element: (
          <VelaroAdminRoute billing>
            <RouteHeader title="Invoices">
              <BillingInvoices />
            </RouteHeader>
          </VelaroAdminRoute>
        )
      },
      {
        path: "/billing/templates",
        element: (
          <VelaroAdminRoute billing>
            <RouteHeader title="Invoice Templates">
              <BillingTemplates />
            </RouteHeader>
          </VelaroAdminRoute>
        )
      },
      {
        path: "/billing/settings",
        element: (
          <VelaroAdminRoute billing>
            <RouteHeader title="Stripe Settings">
              <BillingStripeSettings />
            </RouteHeader>
          </VelaroAdminRoute>
        )
      },
      {
        path: "/billing/qb-mapping",
        element: (
          <VelaroAdminRoute billing>
            <RouteHeader title="QuickBooks Account Mapping">
              <BillingQbMapping />
            </RouteHeader>
          </VelaroAdminRoute>
        )
      },
      {
        path: "/billing/event-log",
        element: (
          <VelaroAdminRoute billing>
            <RouteHeader title="Billing Event Log">
              <BillingEventLog />
            </RouteHeader>
          </VelaroAdminRoute>
        )
      },
      {
        path: "/billing/usage-packs",
        element: (
          <VelaroAdminRoute billing>
            <RouteHeader title="Usage Pack Purchases">
              <BillingUsagePacks />
            </RouteHeader>
          </VelaroAdminRoute>
        )
      },
      {
        path: "/billing/campaign-usage",
        element: (
          <VelaroAdminRoute billing>
            <RouteHeader title="Email Campaign Usage">
              <BillingCampaignUsage />
            </RouteHeader>
          </VelaroAdminRoute>
        )
      },
      {
        path: "/billing/contacts",
        element: (
          <RouteHeader title="Billing Contacts">
            <BillingContacts />
          </RouteHeader>
        )
      },
      {
        path: "/billing/my-contracts",
        element: (
          <RouteHeader title="My Contracts">
            <PendingContracts />
          </RouteHeader>
        )
      },
      {
        path: "/page-widgets",
        element: (
          <StickyRouteHeader title="Page Widgets" subTitle="Target specific pages with inline chat widgets and contextual offers">
            <PageWidgets />
          </StickyRouteHeader>
        )
      },
      {
        path: "/proactive-engagement",
        element: (
          <StickyRouteHeader title="Proactive Invitations" subTitle="Trigger chat invitations automatically based on visitor behavior">
            <ProactiveEngagement />
          </StickyRouteHeader>
        )
      },
      {
        path: "/visitor-monitor",
        element: (
          <RouteHeader title="Visitor Monitor">
            <VisitorMonitor />
          </RouteHeader>
        )
      },
      {
        path: "/email-campaigns",
        element: (
          <StickyRouteHeader title="Email Campaigns" subTitle="Create and send outbound email broadcasts to your contact lists">
            <EmailCampaigns />
          </StickyRouteHeader>
        )
      },
      {
        path: "/ivr-campaigns",
        element: (
          <StickyRouteHeader title="IVR Outbound Campaigns">
            <IvrCampaigns />
          </StickyRouteHeader>
        )
      },
      {
        path: "/appointments",
        element: (
          <StickyRouteHeader title="Appointments" subTitle="Schedule and manage customer appointments">
            <GatedSubscriptionFeature flagKey="enableAppointments" name="Velaro Appointments" meta={INTEGRATION_META.enableAppointments}>
              <Appointments />
            </GatedSubscriptionFeature>
          </StickyRouteHeader>
        )
      },
      {
        path: "/admin/packages",
        element: (
          <VelaroAdminRoute>
            <RouteHeader title="Solution Packages">
              <Packages />
            </RouteHeader>
          </VelaroAdminRoute>
        )
      },
      {
        path: "/support",
        element: (
          <RouteHeader title="Contact Support">
            <ContactSupport />
          </RouteHeader>
        )
      },
      {
        path: "/reports/value",
        element: (
          <StickyRouteHeader title="Value Report" subTitle="Measure ROI, bot deflection rates, and cost savings from AI automation">
            <ValueReport />
          </StickyRouteHeader>
        )
      },
      {
        path: "/reports/agent-performance",
        element: (
          <StickyRouteHeader title="Agent Performance" subTitle="Track agent metrics including handle time, CSAT, and volume">
            <GatedSubscriptionFeature
              flagKey="enableConversationQuality"
              name="Agent Performance"
              description="AI scores every resolved conversation 0-100 with sentiment and a coaching note, then rolls it up per agent."
            >
              <AgentPerformance />
            </GatedSubscriptionFeature>
          </StickyRouteHeader>
        )
      },
      {
        path: "/reports/leaderboard",
        element: (
          <StickyRouteHeader title="Leaderboard" subTitle="Compare agent performance rankings across key metrics">
            <Leaderboard />
          </StickyRouteHeader>
        )
      },
      {
        path: "/reports/chat-intelligence",
        element: (
          <StickyRouteHeader title="Chat Intelligence" subTitle="AI-powered analysis of conversation topics, sentiment, and trends">
            <ChatIntelligence />
          </StickyRouteHeader>
        )
      },
      {
        path: "/reports/bot-effectiveness",
        element: (
          <RouteHeader title="Bot Effectiveness">
            <BotEffectiveness />
          </RouteHeader>
        )
      },
      {
        path: "/reports/csat",
        element: (
          <StickyRouteHeader title="CSAT Results" subTitle="Customer satisfaction scores from post-chat surveys">
            <CsatReport />
          </StickyRouteHeader>
        )
      },
      {
        path: "/reports/conversions",
        element: (
          <StickyRouteHeader title="Conversions" subTitle="Track conversion events attributed to chat interactions">
            <ConversionReport />
          </StickyRouteHeader>
        )
      },
      {
        path: "/reports/apple-messages",
        element: (
          <RouteHeader title="Apple Messages Analytics">
            <AppleMessagesReport />
          </RouteHeader>
        )
      },
      {
        path: "/reports/ivr-campaigns",
        element: (
          <StickyRouteHeader title="IVR Outbound">
            <IvrCampaignReport />
          </StickyRouteHeader>
        )
      },
      {
        path: "/reports/ivr-campaigns/:id",
        element: (
          <StickyRouteHeader title="IVR Outbound">
            <IvrCampaignReport />
          </StickyRouteHeader>
        )
      },
      {
        path: "/reports/call-center",
        element: (
          <StickyRouteHeader title="Call Center Analytics">
            <CallCenterReport />
          </StickyRouteHeader>
        )
      },
      {
        path: "/reports/agent-connectivity",
        element: (
          <StickyRouteHeader title="Agent Connectivity">
            <AgentConnectivityReport />
          </StickyRouteHeader>
        )
      },
      {
        path: "/reports/call-activity",
        element: (
          <StickyRouteHeader title="Call Activity" subTitle="Every call across every provider and origin — agent-dialed, inbound, IVR outbound, and auto-dialer campaigns">
            <CallActivityReport />
          </StickyRouteHeader>
        )
      },
      {
        path: "/reports/call-attribution",
        element: (
          <StickyRouteHeader title="Call Attribution" subTitle="Marketing source, campaign, and medium for every attributed inbound call">
            <GatedAdminFeature flagKey="enableCallRail" name="Call Attribution" meta={INTEGRATION_META.enableCallRail}>
              <CallAttributionReport />
            </GatedAdminFeature>
          </StickyRouteHeader>
        )
      },
      {
        path: "/settings/conversions",
        element: (
          <StickyRouteHeader title="Conversions" subTitle="Track and attribute conversion goals to chat interactions">
            <Conversions />
          </StickyRouteHeader>
        )
      },
      {
        path: "/settings/compliance",
        element: (
          <StickyRouteHeader title="Compliance" subTitle="Manage data retention, privacy settings, and regulatory compliance">
            <CompliancePage />
          </StickyRouteHeader>
        )
      },
      { path: "*", element: <NotFoundPage /> }
    ]
  }
];

const mainLoader = async ({ request }: any) => {
  const url = new URL(request.url);
  const route = url.pathname;
  if (AuthService.isAuthenticated() && route === "/") {
    return redirect("/");
  }
  return null;
};

const appRouter = createBrowserRouter([
  {
    errorElement: <RouteErrorFallback />,
    children: createAppRoutes(mainLoader),
  },
]);

export function AppRoutes() {
  return <RouterProvider router={appRouter} />;
}
